import type { Scope } from "../types";

const SCOPE_STORAGE_KEY = "selected_scope";
const LEGACY_SELECTED_CHATBOT_KEY = "selected_chatbot_id";
const RECENT_SCOPES_KEY = "recent_scopes";
export const RECENT_SCOPES_LIMIT = 5;

export function dedupeStringArray(ids: readonly unknown[] | null | undefined): string[] {
  return [
    ...new Set((ids || []).filter((id): id is string => typeof id === "string" && id.length > 0)),
  ];
}

export function isValidScope(value: unknown): value is Scope {
  if (!value || typeof value !== "object") return false;
  const candidate = value as { type?: unknown; id?: unknown; ids?: unknown };
  if (candidate.type === "all") return true;
  if (candidate.type === "org" || candidate.type === "brand") {
    return typeof candidate.id === "number";
  }
  if (candidate.type === "bot") {
    return typeof candidate.id === "string" && candidate.id.length > 0;
  }
  return (
    candidate.type === "bots" &&
    Array.isArray(candidate.ids) &&
    candidate.ids.length > 0 &&
    candidate.ids.every((id) => typeof id === "string" && id.length > 0)
  );
}

export function scopesEqual(a: Scope | null | undefined, b: Scope | null | undefined): boolean {
  if (a === b) return true;
  if (!a || !b || a.type !== b.type) return false;
  if (a.type === "all") return true;
  if (a.type === "bots") {
    const left = dedupeStringArray(a.ids);
    const right = dedupeStringArray((b as Extract<Scope, { type: "bots" }>).ids);
    return left.length === right.length && right.every((id) => new Set(left).has(id));
  }
  return a.id === (b as Extract<Scope, { type: "org" | "brand" | "bot" }>).id;
}

function parseScope(raw: string | null | undefined): Scope | null {
  if (!raw) return null;
  if (raw === "all") return { type: "all" };
  const colonIndex = raw.indexOf(":");
  if (colonIndex === -1) return null;
  const type = raw.slice(0, colonIndex);
  const id = raw.slice(colonIndex + 1);
  if (type === "org" || type === "brand") {
    const parsed = Number.parseInt(id, 10);
    return Number.isFinite(parsed) ? { type, id: parsed } : null;
  }
  if (type === "bot") {
    const decoded = decodeURIComponent(id);
    return decoded ? { type: "bot", id: decoded } : null;
  }
  if (type === "bots") {
    const ids = dedupeStringArray(id.split(",").map((entry) => decodeURIComponent(entry).trim()));
    return ids.length ? { type: "bots", ids } : null;
  }
  return null;
}

function scopeToParam(scope: Scope): string {
  if (scope.type === "all") return "all";
  if (scope.type === "bots") {
    return `bots:${dedupeStringArray(scope.ids).map(encodeURIComponent).join(",")}`;
  }
  if (scope.type === "bot") return `bot:${encodeURIComponent(scope.id)}`;
  return `${scope.type}:${scope.id}`;
}

export function readScopeFromUrl(): Scope | null {
  if (typeof window === "undefined") return null;
  try {
    return parseScope(new URLSearchParams(window.location.search).get("scope"));
  } catch {
    return null;
  }
}

export function pickInitialScope(): Scope {
  const fromUrl = readScopeFromUrl();
  if (fromUrl) return fromUrl;
  if (typeof window === "undefined") return { type: "all" };
  try {
    const raw = window.localStorage.getItem(SCOPE_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isValidScope(parsed)) return parsed;
    }
    const legacy = window.localStorage.getItem(LEGACY_SELECTED_CHATBOT_KEY);
    if (legacy) {
      const migrated: Scope = legacy === "ALL" ? { type: "all" } : { type: "bot", id: legacy };
      window.localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(migrated));
      return migrated;
    }
  } catch {
    return { type: "all" };
  }
  return { type: "all" };
}

export function readRecentScopes(): Scope[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(RECENT_SCOPES_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter(isValidScope).slice(0, RECENT_SCOPES_LIMIT) : [];
  } catch {
    return [];
  }
}

export function writeScopeToStorage(scope: Scope): void {
  try {
    window.localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(scope));
  } catch {}
}

export function writeRecentScopes(scopes: Scope[]): void {
  try {
    window.localStorage.setItem(RECENT_SCOPES_KEY, JSON.stringify(scopes));
  } catch {}
}

export function writeLegacyStorageKeys(scope: Scope, chatbotIds: string[]): void {
  try {
    const derived =
      scope.type === "bot" ? scope.id : chatbotIds.length === 1 ? chatbotIds[0] : "ALL";
    window.localStorage.setItem(LEGACY_SELECTED_CHATBOT_KEY, derived);
    if (chatbotIds.length) window.localStorage.setItem("chatbot_ids", JSON.stringify(chatbotIds));
  } catch {}
}

/**
 * Writes the scope into the current URL. `location` must be the raw history
 * location (`router.history.location`), not the router state's location: the
 * latter re-serializes the query through the router's search codec, which
 * folds repeated keys such as `chatbot_id=a&chatbot_id=b` into a JSON array
 * and corrupts pages that read them with `URLSearchParams.getAll` (#978).
 */
export function writeScopeToUrl(
  replace: (href: string) => void,
  location: { pathname: string; search?: string; hash?: string },
  scope: Scope,
): void {
  const params = new URLSearchParams(location.search ?? "");
  if (params.get("scope") === scopeToParam(scope)) return;
  params.set("scope", scopeToParam(scope));
  const query = params.toString();
  const rawHash = (location.hash ?? "").replace(/^#/, "");
  const hash = rawHash ? `#${rawHash}` : "";
  replace(`${location.pathname}${query ? `?${query}` : ""}${hash}`);
}
